/**
 * useActiveOrderChecker Hook
 *
 * Centralized hook for checking and managing active orders across all user types.
 * This hook handles the complexity of different order flows based on user roles
 * and order types (Bringzz Pickup, Click & Collect, Mail Delivery).
 *
 * Key Features:
 * - Automatic order checking on mount
 * - Optional polling for real-time updates
 * - Role-specific status mappings
 * - Order type-aware status progression
 */

import { useEffect, useCallback } from 'react';
import useRequest from './useRequest';
import { useAuth } from 'context/AuthContext';
import { useOrder } from 'context/OrderContext';
import { USER_ROLES } from 'utils/constants';

// Order type constants for clarity
// These match the backend OrderRoutes.php order types
const ORDER_TYPES = {
    BRINGZZ_PICKUP: 0,      // Traditional shopper pickup and delivery
    CLICK_AND_COLLECT: 1,   // Customer picks up from pharmacy
    MAIL_DELIVERY: 2        // Courier/postal delivery
};

/**
 * CONSUMER BRINGZZ PICKUP MAPPINGS (Type 0)
 *
 * Maps backend order statuses to UI step numbers for the traditional Bringzz flow:
 * Customer -> Pharmacy -> Shopper -> Customer
 *
 * These indices correspond to customerPickupSegments in constants/orderSegments.js
 *
 * Flow:
 * 1. Customer places order
 * 2. Pharmacy accepts and prepares
 * 3. Shopper picks up prescription from customer
 * 4. Shopper goes to pharmacy
 * 5. Shopper picks up order from pharmacy
 * 6. Shopper delivers to customer
 */
const CONSUMER_BRINGZZ_MAPPINGS = {
    1: 0,   // MATCHING_PHARMACY: Matching pharmacy...
    2: 1,   // PHARMACY_MATCHED: Order is being prepared
    3: 1,   // BEING_PREPARED: Order is being prepared
    4: 2,   // PREPARED: Order is ready
    5: 3,   // SHOPPER_MATCHED: Shopper matched!
    6: 4,   // SHOPPER_ARRIVED_AT_CUSTOMER: Shopper has arrived!
    7: 5,   // SHOPPER_IDENTIFIED_BY_CUSTOMER: On his way to the pharmacy!
    8: 6,   // SHOPPER_ARRIVED_AT_PHARMACY: Shopper arrived at the pharmacy!
    9: 7,   // SHOPPER_IDENTIFIED_BY_PHARMACY: Wait for the shopper!
    10: 8,  // ORDER_CONFIRMED_BY_PHARMACY: Order picked up!
    11: 9,  // ORDER_CONFIRMED_BY_SHOPPER: Shopper on his way to you
    12: 10, // SHOPPER_ARRIVED_AT_CUSTOMER_WITH_PACKAGE: Shopper has arrived!
    13: 11, // SHOPPER_IDENTIFIED_BY_CUSTOMER_WITH_PACKAGE: Wait for the shopper!
    14: 12, // ORDER_CONFIRMED_BY_SHOPPER_WITH_PACKAGE: Order delivered!
    15: 13  // DONE: Order completed!
};

/**
 * CONSUMER CLICK & COLLECT MAPPINGS (Type 1)
 *
 * Maps backend order statuses to UI step numbers for Click & Collect flow:
 * Customer -> Pharmacy -> Customer picks up
 *
 * These indices correspond to customerCollectSegments in constants/orderSegments.js
 *
 * Simplified flow (no shopper involved):
 * 1. Customer places order
 * 2. Pharmacy accepts and prepares
 * 3. Customer goes to pharmacy
 * 4. Customer picks up order
 */
const CONSUMER_COLLECT_MAPPINGS = {
    0: 0,   // OPEN: Order open (fallback to matching)
    1: 0,   // MATCHING_PHARMACY: Matching pharmacy...
    2: 1,   // PHARMACY_MATCHED: Order is being prepared
    3: 1,   // BEING_PREPARED: Order is being prepared
    4: 2,   // PREPARED: Order is ready
    5: 2,   // SHOPPER_MATCHED: Not used in collect flow - show ready
    6: 2,   // SHOPPER_ARRIVED_AT_CUSTOMER: Not used in collect flow - show ready
    7: 2,   // SHOPPER_IDENTIFIED_BY_CUSTOMER: Not used in collect flow - show ready
    8: 3,   // SHOPPER_ARRIVED_AT_PHARMACY: You've arrived at the pharmacy!
    9: 4,   // SHOPPER_IDENTIFIED_BY_PHARMACY: Wait for the pharmacist!
    10: 5,  // ORDER_CONFIRMED_BY_PHARMACY: Order picked up!
    11: 5,  // ORDER_CONFIRMED_BY_SHOPPER: Not used in collect flow - show picked up
    12: 5,  // SHOPPER_ARRIVED_AT_CUSTOMER_WITH_PACKAGE: Not used in collect flow - show picked up
    13: 5,  // SHOPPER_IDENTIFIED_BY_CUSTOMER_WITH_PACKAGE: Not used in collect flow - show picked up
    14: 5,  // ORDER_CONFIRMED_BY_SHOPPER_WITH_PACKAGE: Not used in collect flow - show picked up
    15: 6,  // DONE: Order completed!
    16: 6,  // CANCELED: Order canceled (show completed state)
    17: 0   // ERROR: Error (fallback to matching)
};

/**
 * CONSUMER MAIL DELIVERY MAPPINGS (Type 2)
 *
 * Maps backend order statuses to UI step numbers for Mail/Courier delivery:
 * Customer -> Pharmacy -> Courier -> Customer
 *
 * These indices correspond to customerMailSegments in constants/orderSegments.js
 *
 * Note: This flow reuses some shopper statuses for courier tracking:
 * - ORDER_CONFIRMED_BY_PHARMACY = Courier dispatched
 * - SHOPPER_ARRIVED_AT_CUSTOMER_WITH_PACKAGE = Courier arrived
 */
const CONSUMER_MAIL_MAPPINGS = {
    0: 0,   // OPEN: Order open (fallback to matching)
    1: 0,   // MATCHING_PHARMACY: Matching pharmacy...
    2: 1,   // PHARMACY_MATCHED: Order is being prepared
    3: 1,   // BEING_PREPARED: Order is being prepared
    4: 2,   // PREPARED: Order is ready
    5: 2,   // SHOPPER_MATCHED: Not used in mail flow - show ready
    6: 2,   // SHOPPER_ARRIVED_AT_CUSTOMER: Not used in mail flow - show ready
    7: 2,   // SHOPPER_IDENTIFIED_BY_CUSTOMER: Not used in mail flow - show ready
    8: 2,   // SHOPPER_ARRIVED_AT_PHARMACY: Not used in mail flow - show ready
    9: 2,   // SHOPPER_IDENTIFIED_BY_PHARMACY: Not used in mail flow - show ready
    10: 3,  // ORDER_CONFIRMED_BY_PHARMACY: Courier is on the way!
    11: 3,  // ORDER_CONFIRMED_BY_SHOPPER: Not used in mail flow - show courier dispatched
    12: 4,  // SHOPPER_ARRIVED_AT_CUSTOMER_WITH_PACKAGE: Courier has arrived!
    13: 4,  // SHOPPER_IDENTIFIED_BY_CUSTOMER_WITH_PACKAGE: Not used in mail flow - show courier arrived
    14: 4,  // ORDER_CONFIRMED_BY_SHOPPER_WITH_PACKAGE: Not used in mail flow - show courier arrived
    15: 5,  // DONE: Order completed!
    16: 5,  // CANCELED: Order canceled (show completed state)
    17: 0   // ERROR: Error (fallback to matching)
};

/**
 * PHARMACY ROLE MAPPINGS
 *
 * Maps backend order statuses to UI step numbers for pharmacy users.
 * These mappings are the same regardless of order type, as the pharmacy
 * workflow is consistent: receive -> prepare -> hand over.
 *
 * Used for all pharmacy team members viewing orders.
 */
const PHARMACY_MAPPINGS = {
    2: 0,   // PHARMACY_MATCHED: Get ready to prepare order
    3: 0,   // BEING_PREPARED: Still preparing (stay on same segment)
    4: 1,   // PREPARED: Matching shopper...
    5: 2,   // SHOPPER_MATCHED: Shopper is on his way to the customer
    6: 3,   // SHOPPER_ARRIVED_AT_CUSTOMER: Shopper arrived at the customer
    7: 4,   // SHOPPER_IDENTIFIED_BY_CUSTOMER: Shopper on his way to you
    8: 5,   // SHOPPER_ARRIVED_AT_PHARMACY: Identify Shopper
    9: 6,   // SHOPPER_IDENTIFIED_BY_PHARMACY: Bring the order to the shopper
    10: 7,  // ORDER_CONFIRMED_BY_PHARMACY: Waiting for shopper to confirm
    11: 8,  // ORDER_CONFIRMED_BY_SHOPPER: Get your invoice
    12: 9,  // SHOPPER_ARRIVED_AT_CUSTOMER_WITH_PACKAGE: Get your invoice (stay on invoice)
    13: 9,  // SHOPPER_IDENTIFIED_BY_CUSTOMER_WITH_PACKAGE: Get your invoice (stay on invoice)
    14: 9,  // ORDER_CONFIRMED_BY_SHOPPER_WITH_PACKAGE: Get your invoice (stay on invoice)
    15: 10, // DONE: Order completed!
    16: 10, // CANCELED: Order canceled
    17: 0   // ERROR: Error state
};

/**
 * PHARMACY CLICK & COLLECT MAPPINGS
 *
 * Maps backend order statuses to UI step numbers for pharmacy users handling Click & Collect orders.
 * For Click & Collect, customer comes to pharmacy to pick up order directly.
 *
 * These indices correspond to pharmacyCollectSegments in constants/orderSegments.js
 */
const PHARMACY_COLLECT_MAPPINGS = {
    2: 0,   // PHARMACY_MATCHED: Get ready to prepare order
    3: 0,   // BEING_PREPARED: Still preparing
    4: 1,   // PREPARED: Customer is on his way to you
    5: 1,   // SHOPPER_MATCHED: Not used in collect - show customer on way
    6: 1,   // SHOPPER_ARRIVED_AT_CUSTOMER: Not used in collect - show customer on way
    7: 1,   // SHOPPER_IDENTIFIED_BY_CUSTOMER: Not used in collect - show customer on way
    8: 2,   // SHOPPER_ARRIVED_AT_PHARMACY: Customer arrived at the pharmacy
    9: 3,   // SHOPPER_IDENTIFIED_BY_PHARMACY: Customer identified, get his bag
    10: 4,  // ORDER_CONFIRMED_BY_PHARMACY: Waiting for customer to confirm
    11: 5,  // ORDER_CONFIRMED_BY_SHOPPER: Not used in collect - show invoice
    12: 5,  // SHOPPER_ARRIVED_AT_CUSTOMER_WITH_PACKAGE: Not used - show invoice
    13: 5,  // SHOPPER_IDENTIFIED_BY_CUSTOMER_WITH_PACKAGE: Not used - show invoice
    14: 5,  // ORDER_CONFIRMED_BY_SHOPPER_WITH_PACKAGE: Not used - show invoice
    15: 6,  // DONE: Order completed
    16: 6,  // CANCELED: Order canceled
    17: 0   // ERROR: Error state
};

/**
 * PHARMACY MAIL DELIVERY MAPPINGS
 *
 * Maps backend order statuses to UI step numbers for pharmacy users handling mail delivery orders.
 * For mail delivery, pharmacy hands over packages to logistics partners instead of shoppers.
 */
const PHARMACY_MAIL_MAPPINGS = {
    2: 0,   // PHARMACY_MATCHED: Get ready to prepare order
    3: 0,   // BEING_PREPARED: Still preparing
    4: 1,   // PREPARED: Handover order to logistic partner
    10: 2,  // ORDER_CONFIRMED_BY_PHARMACY: Shipping in progress
    12: 3,  // SHOPPER_ARRIVED_AT_CUSTOMER_WITH_PACKAGE: Get your invoice
    15: 4   // DONE: Order completed
};

/**
 * SHOPPER ROLE MAPPINGS
 *
 * Maps backend order statuses to UI step numbers for shopper/delivery users.
 * These mappings handle the full shopper journey from acceptance to delivery.
 *
 * Note: Only applies to BRINGZZ_PICKUP orders, as other types don't use shoppers.
 */
const SHOPPER_MAPPINGS = {
    5: 0,   // SHOPPER_MATCHED: Accepted order
    6: 1,   // SHOPPER_ARRIVED_AT_CUSTOMER
    7: 2,   // SHOPPER_IDENTIFIED_BY_CUSTOMER
    8: 3,   // SHOPPER_ARRIVED_AT_PHARMACY
    9: 4,   // SHOPPER_IDENTIFIED_BY_PHARMACY
    10: 5,  // ORDER_CONFIRMED_BY_PHARMACY
    11: 6,  // ORDER_CONFIRMED_BY_SHOPPER
    12: 7,  // SHOPPER_ARRIVED_AT_CUSTOMER_WITH_PACKAGE
    13: 8,  // SHOPPER_IDENTIFIED_BY_CUSTOMER_WITH_PACKAGE
    14: 9,  // ORDER_CONFIRMED_BY_SHOPPER_WITH_PACKAGE
    15: 10  // DONE
};

/**
 * SHOPPER MAIL DELIVERY MAPPINGS
 *
 * Maps backend order statuses to UI step numbers for shoppers handling mail delivery orders.
 * For mail delivery, shoppers hand over packages to logistics partners instead of delivering directly.
 */
const SHOPPER_MAIL_MAPPINGS = {
    5: 0,   // SHOPPER_MATCHED: Accepted order
    6: 1,   // SHOPPER_ARRIVED_AT_CUSTOMER
    7: 2,   // SHOPPER_IDENTIFIED_BY_CUSTOMER
    8: 3,   // SHOPPER_ARRIVED_AT_PHARMACY
    4: 4,   // PREPARED: Handover order to logistic partner
    9: 4,   // SHOPPER_IDENTIFIED_BY_PHARMACY
    10: 5,  // ORDER_CONFIRMED_BY_PHARMACY
    11: 6,  // ORDER_CONFIRMED_BY_SHOPPER
    15: 10  // DONE
};

/**
 * Determines if an order should be actively processed/tracked by the user
 *
 * Different roles stop tracking orders at different points:
 * - Pharmacy: Stops after handing to shopper
 * - Shopper: Stops after final delivery
 * - Consumer: Tracks until completion
 *
 * @param {number} userRole - User's role from USER_ROLES
 * @param {number} deliveryStatus - Current order status from ORDER_STATUS
 * @returns {boolean} Whether to continue processing this order
 */
const shouldProcessOrder = (userRole, deliveryStatus) => {
    switch (userRole) {
        case USER_ROLES.PHARMACY_ROLE:
            // Pharmacy processes orders until shopper confirms pickup
            return deliveryStatus < 11; // ORDER_CONFIRMED_BY_SHOPPER

        case USER_ROLES.SHOPPER_ROLE:
            // Shopper processes orders until final delivery confirmation
            return deliveryStatus < 14; // ORDER_CONFIRMED_BY_SHOPPER_WITH_PACKAGE

        case USER_ROLES.CONSUMER_ROLE:
            // Consumer can track all statuses except completed ones
            return deliveryStatus < 15; // DONE

        default:
            return false;
    }
};

/**
 * Determines the navigation path for viewing order details
 *
 * Routes users to role-specific and order-type-specific pages
 *
 * @param {number} userRole - User's role
 * @param {Object} orderData - Order data including type and status
 * @returns {string} The route path to navigate to
 */
const getNavigationPath = (userRole, orderData) => {
    const { type, deliveryStatus } = orderData;

    switch (userRole) {
        case USER_ROLES.PHARMACY_ROLE:
            // For Click & Collect orders when customer arrives
            if (type === 1 && deliveryStatus >= 8) { // SHOPPER_ARRIVED_AT_PHARMACY
                return '/pharmacy/collect-order';
            }
            return '/pharmacy/order-details';

        case USER_ROLES.SHOPPER_ROLE:
            return '/shopper/delivery';

        case USER_ROLES.CONSUMER_ROLE:
            // Different paths based on delivery type
            if (type === 0) { // Bringzz Pickup
                return '/consumer/track-order';
            } else if (type === 1) { // Click & Collect
                return '/consumer/collect-order';
            }
            return '/consumer/order-details';

        default:
            return '/order-details';
    }
};

/**
 * Get the appropriate status mapping based on user role and order type
 *
 * This is the core function that selects which mapping to use.
 * Pharmacy and Shopper roles use the same mappings regardless of order type,
 * while Consumer mappings vary significantly based on the delivery method.
 *
 * @param {number} userRole - The user's role (PHARMACY, SHOPPER, or CONSUMER)
 * @param {number} orderType - The order type (0: Bringzz, 1: Collect, 2: Mail)
 * @returns {Object} Mapping object of ORDER_STATUS -> UI step number
 *
 * @example
 * // For a consumer with a mail delivery order at status 10:
 * const mapping = getStatusMapping(USER_ROLES.CONSUMER_ROLE, 2);
 * const uiStep = mapping[10]; // ORDER_CONFIRMED_BY_PHARMACY - Returns 3
 */
const getStatusMapping = (userRole, orderType) => {
    // Pharmacy mappings change based on order type
    if (userRole === USER_ROLES.PHARMACY_ROLE) {
        switch (orderType) {
            case ORDER_TYPES.MAIL_DELIVERY:  // 2
                return PHARMACY_MAIL_MAPPINGS;
            case ORDER_TYPES.CLICK_AND_COLLECT:  // 1
                return PHARMACY_COLLECT_MAPPINGS;
            case ORDER_TYPES.BRINGZZ_PICKUP:  // 0
            default:
                return PHARMACY_MAPPINGS;
        }
    }

    // Shopper mappings change for mail delivery orders
    if (userRole === USER_ROLES.SHOPPER_ROLE) {
        if (orderType === ORDER_TYPES.MAIL_DELIVERY) {
            return SHOPPER_MAIL_MAPPINGS;
        }
        return SHOPPER_MAPPINGS;
    }

    // Consumer mappings are order-type specific due to different delivery flows
    if (userRole === USER_ROLES.CONSUMER_ROLE) {
        switch (orderType) {
            case ORDER_TYPES.MAIL_DELIVERY:  // 2
                return CONSUMER_MAIL_MAPPINGS;
            case ORDER_TYPES.CLICK_AND_COLLECT:  // 1
                console.log('Using CONSUMER_COLLECT_MAPPINGS for type', orderType);
                return CONSUMER_COLLECT_MAPPINGS;
            case ORDER_TYPES.BRINGZZ_PICKUP:  // 0
            default:
                return CONSUMER_BRINGZZ_MAPPINGS;
        }
    }

    // Default fallback for unknown roles
    return {};
};

/**
 * Custom hook for checking and handling active orders
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoCheck - Whether to automatically check on mount
 * @param {function} options.onActiveOrder - Callback when active order is found
 * @param {function} options.onNoActiveOrder - Callback when no active order is found
 * @param {number} options.pollInterval - Interval in ms to poll for updates (0 = no polling)
 */
const useActiveOrderChecker = (options = {}) => {
    const {
        autoCheck = true,
        onActiveOrder,
        onNoActiveOrder,
        pollInterval = 0
    } = options;

    const { sendRequest, loading } = useRequest();
    const { user } = useAuth();
    const { startMatchingProcess, resetOrderContext } = useOrder();

    /**
     * Internal handler for processing active order responses
     *
     * This function:
     * 1. Validates the response
     * 2. Determines the correct status mapping based on role and order type
     * 3. Calculates the current UI step
     * 4. Updates the order context if the order should be processed
     * 5. Calls the appropriate callback (onActiveOrder or onNoActiveOrder)
     */
    const handleActiveOrder = useCallback((response) => {
        if (!user || !response || response.status !== 'success') {
            console.log('No active orders or invalid response');
            if (onNoActiveOrder) {
                onNoActiveOrder();
            }
            return;
        }

        const orderData = response.data;
        const userRole = user.userRole;
        const orderType = orderData.type || 0; // Default to Bringzz Pickup if type is missing

        // Select the appropriate mapping based on user role and order type
        const statusMapping = getStatusMapping(userRole, orderType);

        if (!statusMapping || Object.keys(statusMapping).length === 0) {
            console.error('Invalid user role or order type for status mapping:', { userRole, orderType });
            return;
        }

        // Map the backend status to a UI step number
        // Convert to string to ensure proper lookup since object keys are strings
        const statusKey = String(orderData.deliveryStatus);
        const uiStep = statusMapping[statusKey];

        // Debug logging
        console.log('ActiveOrderChecker mapping:', {
            userRole,
            orderType,
            deliveryStatus: orderData.deliveryStatus,
            statusKey: statusKey,
            uiStep,
            mappingValue: statusMapping[statusKey],
            hasMapping: statusKey in statusMapping,
            allMappingKeys: Object.keys(statusMapping)
        });

        // Check if this order is still relevant for the user's role
        if (shouldProcessOrder(userRole, orderData.deliveryStatus)) {
            // Update the global order context with current data and UI step
            // Use 0 only if uiStep is truly undefined, not if it's 0
            const finalStep = uiStep !== undefined ? uiStep : 0;

            console.log('About to call startMatchingProcess:', {
                deliveryStatus: orderData.deliveryStatus,
                orderType: orderType,
                statusKey: statusKey,
                uiStep: uiStep,
                finalStep: finalStep,
                mappingForStatus2: statusMapping['2'],
                mappingForStatus2AsNum: statusMapping[2],
                typeOfUiStep: typeof uiStep,
                isUndefined: uiStep === undefined,
                isNull: uiStep === null
            });

            startMatchingProcess(orderData, finalStep);

            // Notify the parent component about the active order
            if (onActiveOrder) {
                onActiveOrder({
                    order: orderData,
                    step: finalStep,
                    navigationPath: getNavigationPath(userRole, orderData),
                    orderType: orderType
                });
            }
        } else {
            // Order has progressed beyond this user's involvement
            console.log('Order in terminal state, not processing');
            resetOrderContext();
            if (onNoActiveOrder) {
                onNoActiveOrder();
            }
        }
    }, [user, startMatchingProcess, resetOrderContext, onActiveOrder, onNoActiveOrder]);

    const checkForActiveOrders = useCallback(async () => {
        try {
            const response = await sendRequest({
                method: 'GET',
                url: '/module/order/checkForActiveOrders',
            });

            handleActiveOrder(response);
            return response;
        } catch (error) {
            console.error('Error checking for active orders:', error);
            if (onNoActiveOrder) {
                onNoActiveOrder();
            }
            return null;
        }
    }, [sendRequest, handleActiveOrder, onNoActiveOrder]);

    // Auto-check on mount if enabled
    useEffect(() => {
        if (autoCheck && user) {
            checkForActiveOrders();
        }
    }, [autoCheck, user]);

    // Set up polling if interval is specified
    useEffect(() => {
        if (pollInterval > 0 && user) {
            const intervalId = setInterval(() => {
                checkForActiveOrders();
            }, pollInterval);

            return () => clearInterval(intervalId);
        }
    }, [pollInterval, user, checkForActiveOrders]);

    return {
        checkForActiveOrders,
        loading,
        getStatusMapping: (orderType) => {
            const mapping = getStatusMapping(user?.userRole, orderType);
            console.log('Hook getStatusMapping called:', { userRole: user?.userRole, orderType, mapping });
            return mapping;
        },
        shouldProcessOrder: (deliveryStatus) => shouldProcessOrder(user?.userRole, deliveryStatus)
    };
};

export default useActiveOrderChecker;
export {
    getStatusMapping,
    shouldProcessOrder,
    getNavigationPath,
    ORDER_TYPES,
    CONSUMER_BRINGZZ_MAPPINGS,
    CONSUMER_COLLECT_MAPPINGS,
    CONSUMER_MAIL_MAPPINGS,
    PHARMACY_MAPPINGS,
    PHARMACY_COLLECT_MAPPINGS,
    PHARMACY_MAIL_MAPPINGS,
    SHOPPER_MAPPINGS,
    SHOPPER_MAIL_MAPPINGS
};